import { Key, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Flex, Table } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { path } from 'src/libs'
import { showNotification } from 'src/features/notifications'
import { BudgetRequest } from 'src/pages/budget/budgetRequests/types'
import {
  FilterTable,
  getRequestListColumns,
} from 'src/pages/budget/budgetRequests/components'
import {
  filtersChangedBudgetListOwn,
  openModalRequestsCopy,
  openModalRequestsDelete,
  openModalRequestsToSubmit,
} from 'src/pages/budget/budgetRequests/model'
import { PAGE_SIZE } from 'src/pages/budget/budgetCategories/const'
import { STATUS, STATUS_PERIOD } from 'src/pages/budget/const'
import { useBudgetPeriod } from 'src/pages/budget/budgetRequests/hooks'
import { TStatusPeriod } from 'src/pages/budget/types'

type Props = {
  list: BudgetRequest[]
  filterList: BudgetRequest[]
  currentPeriodStatus?: TStatusPeriod
}

export const OwnTable = ({ list, filterList, currentPeriodStatus }: Props) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([])

  const { budgetPeriod } = useBudgetPeriod()
  const { currentPeriodId } = budgetPeriod
  const isOutOfBudgetRequest =
    currentPeriodStatus !== STATUS_PERIOD.OPEN_CREATE

  const hasSelected = selectedRowKeys.length > 0
  const navigate = useNavigate()

  const selectedRequests = list.filter(({ id }) => selectedRowKeys.includes(id))
  const notDraftIds = selectedRequests
    .filter(({ status }) => status !== STATUS.DRAFT)
    .map(({ id }) => id)

  const checkDelete = () => {
    if (notDraftIds.length > 0) {
      showNotification({
        type: 'warning',
        message: 'Нельзя удалить выбранные заявки',
        description: `Заявки №${notDraftIds.join(
          ', ',
        )} не находятся в статусе «Черновик»`,
        duration: 15,
      })
      return
    }
    openModalRequestsDelete({ items: selectedRequests })
  }

  const checkApprove = () => {
    if (notDraftIds.length > 0) {
      showNotification({
        type: 'warning',
        message: 'Нельзя отправить выбранные заявки на согласование',
        description: `Заявки №${notDraftIds.join(
          ', ',
        )} не находятся в статусе «Черновик»`,
        duration: 15,
      })
      return
    }
    openModalRequestsToSubmit({ items: selectedRequests })
  }

  return (
    <Flex vertical gap="middle" style={{ width: '100%' }}>
      <Flex justify="space-between" gap={5}>
        <Flex gap={5} wrap>
          {currentPeriodStatus !== STATUS_PERIOD.CLOSED && (
            <Button
              size="middle"
              color="geekblue"
              variant={isOutOfBudgetRequest ? 'outlined' : 'solid'}
              icon={<PlusOutlined />}
              iconPosition="start"
              key="btn_add_budget_request_1"
              onClick={() =>
                navigate(path.budget.budgets.create(currentPeriodId))
              }
            >
              {isOutOfBudgetRequest ? 'Заявка вне бюджета' : 'Новая заявка'}
            </Button>
          )}

          {hasSelected && (
            <>
              <Button
                size="middle"
                color="cyan"
                variant="solid"
                key="btn_add_to_submitted"
                onClick={checkApprove}
              >
                Отправить на согласование ({selectedRowKeys.length})
              </Button>
              <Button
                size="middle"
                color="volcano"
                variant="solid"
                key="btn_delete_budget_request_1"
                onClick={checkDelete}
              >
                Удалить ({selectedRowKeys.length})
              </Button>
              <Button
                size="middle"
                color="geekblue"
                variant="solid"
                key="btn_copy_budget_request"
                onClick={() =>
                  openModalRequestsCopy({ items: selectedRequests })
                }
              >
                Скопировать ({selectedRowKeys.length})
              </Button>
            </>
          )}
        </Flex>
        <FilterTable
          fields={['articleName', 'status', 'products', 'searchText']}
          optionsStatus={[STATUS.DRAFT, STATUS.SUBMITTED, STATUS.IN_REVIEW]}
          onChange={(value) => {
            setSelectedRowKeys([])
            filtersChangedBudgetListOwn(value)
          }}
          list={list}
        />
      </Flex>
      <Table
        dataSource={filterList}
        tableLayout="fixed"
        rowSelection={{
          type: 'checkbox',
          columnWidth: 32,
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys),
        }}
        pagination={
          filterList.length > PAGE_SIZE
            ? {
                total: filterList?.length,
                defaultPageSize: PAGE_SIZE,
                showSizeChanger: true,
                position: ['bottomCenter'],
              }
            : false
        }
        columns={getRequestListColumns(
          [
            'id',
            'articleName',
            'itemName',
            'description',
            'amount',
            'productIds',
            'status',
            'actionOwn',
          ],
          currentPeriodStatus,
          false,
          true,
        )}
        rowKey={(record) => record.id}
        rowClassName={(_, index) =>
          `ant_table_row_height ${index % 2 ? 'even-row' : 'odd-row'}`
        }
      />
    </Flex>
  )
}
